/**
 * Site-Specific Research Provider — uses AI + Google Search to find
 * location-specific facts that static APIs can't provide.
 *
 * For a given lat/lng, researches:
 * - State/local water rights and appropriation rules
 * - Tribal land proximity and consultation requirements
 * - Local environmental constraints (endangered species, protected areas)
 * - Nearby industrial/military facilities affecting siting
 * - State-specific permitting requirements
 * - Local utility provider and rate structure
 * - Regional construction labor market
 * - Relevant historical siting precedents
 *
 * Results are cached per grid cell and persisted as part of the SED.
 */
import type { DataProvider, ProviderResult } from './types';
import { CACHE_TTL, gridCellKey } from './types';
import { Type, runGeminiJSON } from '../geminiClient';
import { AI_STACK_LABEL_VERSIONED } from '../../config/branding';
import { throwIfAborted } from '../../lib/abortSignal';

/**
 * PH-A1.7 — Per-report framing for the AI Site Research prompt.
 *
 * The legacy prompt hardcoded "nuclear power + data center siting
 * assessment", so environmental / water-resource / test-fit reports
 * got research scoped to a workload that may not be in play. The
 * framing line is the first sentence of the prompt and steers the
 * model's emphasis on every subsequent topic (water rights, hazards,
 * permitting).
 *
 * Defaults to `'feasibility-style assessment'` when the reportType is
 * unknown — keeps the legacy SED-bootstrap behaviour intact (bootstrap
 * runs before the user picks a report type so the cached result has to
 * be report-type-agnostic). Exported so the framing table can be
 * exercised by unit tests without invoking Gemini.
 */
export const SITE_RESEARCH_REPORT_FRAMING: Record<string, string> = {
  feasibility:         'industrial development and data center screening',
  environmental:       'environmental impact assessment',
  'water-resource':    'water-resource assessment',
  'power-availability': 'power-availability assessment',
  interconnection:     'grid-interconnection feasibility assessment',
  'test-fit':          'pre-engineering test-fit screening',
};

/** Fallback when `reportType` is undefined or unknown. */
export const SITE_RESEARCH_DEFAULT_FRAMING = 'feasibility-style assessment';

/**
 * Build the location-specific research prompt for a given coordinate
 * and report-type framing.
 *
 * Exported so the framing table can be exercised directly by unit tests
 * — the full provider.fetch path requires Gemini and would otherwise
 * make the framing regression risk invisible to CI.
 */
export function buildSiteResearchPrompt(
  lat: number,
  lng: number,
  reportType?: string,
): string {
  const framing = (typeof reportType === 'string' && SITE_RESEARCH_REPORT_FRAMING[reportType])
    || SITE_RESEARCH_DEFAULT_FRAMING;
  return `Research the specific location at coordinates ${lat.toFixed(4)}, ${lng.toFixed(4)} for a ${framing}. Find ONLY verifiable, location-specific facts.

Research these topics for THIS SPECIFIC LOCATION:

1. WATER RIGHTS: What water law doctrine does this state use (prior appropriation, riparian, hybrid)? What regulatory body manages water rights? What are key constraints for large industrial water users? What is the nearest significant water source (river, reservoir, aquifer)?

2. TRIBAL LANDS: Are there any Native American tribal lands, reservations, or culturally significant sites within 50 miles? Identify documented tribal locations as regional context only. Coordinates or distance do not establish a federal undertaking, an agency consultation duty, or project-specific applicability. Set consultationRequired to null.

3. LOCAL ENVIRONMENTAL: Are there any state-level protected areas, wildlife refuges, or conservation easements nearby? Is this area in an EPA attainment or non-attainment zone? Any state-specific environmental constraints?

4. NEARBY HAZARDS: List any airports within 10 miles, military installations within 20 miles, major industrial facilities (chemical plants, refineries), and dams upstream.

5. LOCAL INFRASTRUCTURE: Who is the electric utility serving this area? What is the nearest interstate highway? Is there rail access? Nearest port or barge access?

6. STATE PERMITTING: Identify potentially relevant state regulatory programs with primary-source URLs and effective dates. Do not assume nuclear technology, a federal action, project emissions, wetland jurisdiction, or permit thresholds. Do not assign required permits or a project permitting timeline from coordinates; state that applicability and duration are unresolved.

7. PRECEDENTS: Are there any existing or proposed nuclear facilities, data centers, or large industrial projects in this region? Any relevant siting precedents?

Distinguish regional research context from parcel-verified facts and project-specific agency determinations. For each factual statement include the primary-source URL and source date in its text; leave unsupported information unknown. Nearby infrastructure does not establish site service or capacity. Be specific to THIS location. Do not provide generic US-wide information.`;
}

export interface SiteResearchData {
  /** Detached report projection; raw source SED is unchanged. */
  reportSourceProjection?: { reason: string; sourceUrls: string[]; regionalLeads?: string[] };
  /** Coordinate-only research cannot establish project-specific legal duties. */
  applicability?: { status: 'unresolved'; reason: string };
  /** Retained original model assertions for audit, never operative authority. */
  researchStatements?: { consultationRequired: boolean | null; estimatedTimeline: string; negativeListStatements?: Record<string, string[]> };
  waterRights: {
    doctrine: string;        // "prior appropriation", "riparian", "hybrid"
    authority: string;       // regulatory body
    constraints: string;     // key limitations
    nearestWaterSource: string;
  };
  tribalLands: {
    nearbyTribes: string[];
    consultationRequired: boolean | null;
    details: string;
  };
  localEnvironmental: {
    protectedAreas: string[];
    stateConstraints: string;
    airQualityRegion: string; // attainment/non-attainment
  };
  nearbyHazards: {
    airports: string[];      // within 10 miles
    militaryBases: string[];
    industrialFacilities: string[];
    dams: string[];
  };
  localInfrastructure: {
    utilityProvider: string;
    nearestInterstate: string;
    railAccess: string;
    portAccess: string;
  };
  statePermitting: {
    nuclearMoratorium: boolean | null;
    keyPermits: string[];
    estimatedTimeline: string;
  };
  precedents: {
    description: string;
    relevance: string;
  }[];
}

/** Qualifies fresh and legacy coordinate research without deleting its source context. */
export function qualifySiteResearchContext(data: SiteResearchData): SiteResearchData {
  const negative = (value: string) => /^(?:no\b|none\b|not\s+(?:identified|available)|unknown\b)/i.test(value.trim());
  const negativeListStatements = Object.fromEntries(Object.entries(data.nearbyHazards ?? {})
    .map(([key, values]) => [key, values.filter(negative)]).filter(([, values]) => (values as string[]).length > 0));
  return {
    ...data,
    applicability: { status: 'unresolved', reason: 'Coordinate-based AI research is regional context. Project-specific permit applicability, federal nexus, consultation duties, service territory and schedules require independent source verification.' },
    researchStatements: data.researchStatements ?? {
      consultationRequired: data.tribalLands?.consultationRequired ?? null,
      estimatedTimeline: data.statePermitting?.estimatedTimeline ?? '',
      ...(Object.keys(negativeListStatements).length ? { negativeListStatements } : {}),
    },
    nearbyHazards: Object.fromEntries(Object.entries(data.nearbyHazards ?? {}).map(([key, values]) => [key, values.filter(value => !negative(value))])) as SiteResearchData['nearbyHazards'],
    tribalLands: { ...data.tribalLands, consultationRequired: null },
    statePermitting: { ...data.statePermitting, estimatedTimeline: 'Project-specific permitting duration is unresolved.' },
  };
}

export function researchMoratoriumLabel(value: boolean | null | undefined): string {
  return value === true ? 'Nuclear moratorium' : value === false ? 'No nuclear moratorium' : 'Nuclear moratorium status unverified';
}

export function researchConsultationLabel(value: boolean | null | undefined): string {
  return value === true ? 'Required' : value === false ? 'Not required' : 'Applicability unresolved';
}

// PH-research-cache-bounded — in-memory cache for site-research results.
//
// Previously a raw `Map<string, { data, ts }>` with no eviction: every unique
// grid cell ever queried in the lifetime of the Cloud Run instance accumulated
// forever. A long-running pod handling thousands of distinct sites bloated
// process memory and could eventually OOM. Site research changes slowly so the
// TTL stays generous (30 days), but we also cap entry count and evict
// least-recently-used (LRU via Map insertion-order rotation).
//
// Pattern note: `dataProviders/cache.ts` is Firestore-backed and not reusable
// for an in-memory tier, so the bounded cache lives inline here.
const RESEARCH_CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const RESEARCH_CACHE_MAX_ENTRIES = 500;

interface BoundedTtlCacheEntry<T> { data: T; ts: number }

/**
 * Minimal LRU + TTL cache backed by a `Map`'s insertion-order semantics.
 *
 * - `get` returns the entry only if it is still within TTL; expired entries are
 *   evicted on read. On a fresh hit the entry is re-inserted to refresh its
 *   LRU position (most-recently-used last).
 * - `set` evicts the oldest entry (first in iteration order) once `maxEntries`
 *   is exceeded so the cache size is bounded.
 *
 * Exposed via the `_researchCache` constant export so unit tests can exercise
 * the eviction behaviour without going through the provider's Gemini call.
 */
export class BoundedTtlCache<T> {
  private readonly store = new Map<string, BoundedTtlCacheEntry<T>>();

  constructor(
    private readonly maxEntries: number,
    private readonly ttlMs: number,
    private readonly now: () => number = () => Date.now(),
  ) {
    if (maxEntries <= 0) throw new Error('BoundedTtlCache: maxEntries must be > 0');
    if (ttlMs <= 0) throw new Error('BoundedTtlCache: ttlMs must be > 0');
  }

  get(key: string): T | undefined {
    const entry = this.store.get(key);
    if (!entry) return undefined;
    if (this.now() - entry.ts >= this.ttlMs) {
      this.store.delete(key);
      return undefined;
    }
    // Refresh LRU position — re-insertion moves the key to the end.
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.data;
  }

  set(key: string, data: T): void {
    // If the key already exists, remove it first so re-insertion bumps LRU.
    if (this.store.has(key)) this.store.delete(key);
    this.store.set(key, { data, ts: this.now() });
    while (this.store.size > this.maxEntries) {
      const oldest = this.store.keys().next().value;
      if (oldest === undefined) break;
      this.store.delete(oldest);
    }
  }

  /** Test / introspection helper — current entry count. */
  size(): number { return this.store.size; }

  /** Test helper — does the cache currently hold this key? */
  has(key: string): boolean { return this.store.has(key); }

  /** Test helper — clear all entries. Not used at runtime. */
  clear(): void { this.store.clear(); }
}

// In-memory cache — site research changes very slowly. Exported (with a
// leading underscore to flag "module-internal, do not poke from app code") so
// unit tests can directly verify eviction behaviour.
export const _researchCache = new BoundedTtlCache<SiteResearchData>(
  RESEARCH_CACHE_MAX_ENTRIES,
  RESEARCH_CACHE_TTL,
);

export const siteResearchProvider: DataProvider<SiteResearchData> = {
  id: 'site-research',
  cacheKey: ({ lat, lng, geo }) => `${gridCellKey(lat, lng)}:context-v2:${geo?.reportType ?? 'generic'}`,
  name: 'AI Site-Specific Research',
  cacheTtlMs: CACHE_TTL.CLIMATE_NORMALS,
  requiresKey: false,

  async fetch(lat, lng, _apiKey, geo): Promise<ProviderResult<SiteResearchData>> {
    throwIfAborted(geo?.signal);
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`site-research` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. The AI keys its location-specific research off the coordinates
    // embedded in the prompt below (and stamps them into the source string),
    // so a kilometre of drift can shift the watershed / jurisdiction it
    // researches — the parcel centroid is the right point.
    //
    // PH-A1.7 — `geo.reportType` parametrizes the prompt framing.
    // Undefined (e.g. SED bootstrap, before the user picks a report)
    // → default feasibility-style framing, preserving the cached
    // SED-level behaviour. Per-report invocation can override.
    const cacheKey = `${gridCellKey(lat, lng)}:context-v2:${geo?.reportType ?? 'generic'}`;
    // BoundedTtlCache.get returns the data only if still within TTL — the TTL
    // and expiry checks live inside the cache abstraction now, so the call
    // site no longer needs to compare timestamps.
    const cached = _researchCache.get(cacheKey);
    if (cached) {
      return {
        data: qualifySiteResearchContext(cached),
        sources: [{
          field: 'siteResearch',
          source: `AI Site Research (cached) — location-specific regulatory, environmental, and infrastructure context via ${AI_STACK_LABEL_VERSIONED}`,
          retrievedAt: new Date().toISOString(),
        }],
      };
    }

    // Routes through the shared Gemini client → /api/gemini-proxy with the
    // Firebase ID token + personal-key header injected by the fetch patch
    // installed at geminiClient.ts module load. Constructing the SDK
    // locally here is what produced the {error:"Authentication required"}
    // bug — the patch only attaches when geminiClient is imported.
    const contents = buildSiteResearchPrompt(lat, lng, geo?.reportType);

    const schema = {
      type: Type.OBJECT,
      properties: {
        waterRights: {
          type: Type.OBJECT,
          properties: {
            doctrine: { type: Type.STRING },
            authority: { type: Type.STRING },
            constraints: { type: Type.STRING },
            nearestWaterSource: { type: Type.STRING },
          },
          required: ['doctrine', 'authority', 'constraints', 'nearestWaterSource'],
        },
        tribalLands: {
          type: Type.OBJECT,
          properties: {
            nearbyTribes: { type: Type.ARRAY, items: { type: Type.STRING } },
            consultationRequired: { type: Type.BOOLEAN, nullable: true },
            details: { type: Type.STRING },
          },
          required: ['nearbyTribes', 'consultationRequired', 'details'],
        },
        localEnvironmental: {
          type: Type.OBJECT,
          properties: {
            protectedAreas: { type: Type.ARRAY, items: { type: Type.STRING } },
            stateConstraints: { type: Type.STRING },
            airQualityRegion: { type: Type.STRING },
          },
          required: ['protectedAreas', 'stateConstraints', 'airQualityRegion'],
        },
        nearbyHazards: {
          type: Type.OBJECT,
          properties: {
            airports: { type: Type.ARRAY, items: { type: Type.STRING } },
            militaryBases: { type: Type.ARRAY, items: { type: Type.STRING } },
            industrialFacilities: { type: Type.ARRAY, items: { type: Type.STRING } },
            dams: { type: Type.ARRAY, items: { type: Type.STRING } },
          },
          required: ['airports', 'militaryBases', 'industrialFacilities', 'dams'],
        },
        localInfrastructure: {
          type: Type.OBJECT,
          properties: {
            utilityProvider: { type: Type.STRING },
            nearestInterstate: { type: Type.STRING },
            railAccess: { type: Type.STRING },
            portAccess: { type: Type.STRING },
          },
          required: ['utilityProvider', 'nearestInterstate', 'railAccess', 'portAccess'],
        },
        statePermitting: {
          type: Type.OBJECT,
          properties: {
            nuclearMoratorium: { type: Type.BOOLEAN },
            keyPermits: { type: Type.ARRAY, items: { type: Type.STRING } },
            estimatedTimeline: { type: Type.STRING },
          },
          required: ['nuclearMoratorium', 'keyPermits', 'estimatedTimeline'],
        },
        precedents: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              description: { type: Type.STRING },
              relevance: { type: Type.STRING },
            },
          },
        },
      },
      required: ['waterRights', 'tribalLands', 'localEnvironmental', 'nearbyHazards', 'localInfrastructure', 'statePermitting', 'precedents'],
    };

    const rawData = await runGeminiJSON<SiteResearchData>({
      contents,
      schema,
      timeoutMs: 120_000,
      signal: geo?.signal,
    });
    throwIfAborted(geo?.signal);

    // Validate minimum structure — the schema above keeps Gemini's response
    // shape close to SiteResearchData, but a malformed response can still
    // pass JSON.parse without populating the required nested objects.
    if (!rawData.waterRights || !rawData.localInfrastructure) {
      throw new Error('Site research returned incomplete data');
    }

    const data = qualifySiteResearchContext(rawData);

    // Cache result — BoundedTtlCache stamps `ts` internally and evicts the
    // LRU entry when the size cap is exceeded.
    _researchCache.set(cacheKey, data);

    return {
      data,
      sources: [{
        field: 'siteResearch',
        source: `AI Site Research — location-specific context for ${lat.toFixed(4)}, ${lng.toFixed(4)} via ${AI_STACK_LABEL_VERSIONED}`,
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
